import { ArrowLeftIcon, EnvelopeIcon, PaperAirplaneIcon, UserPlusIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { PROVIDER_TYPE_LABEL, ProviderType } from './mockData'

type Mode = 'invite' | 'manual'

export default function AddProvider() {
    const navigate = useNavigate()
    const [mode, setMode] = useState<Mode>('invite')
    const [type, setType] = useState<ProviderType>('aba-bcba')

    return (
        <div className='max-w-3xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to='/admin/family-care-team-preview/team'
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                Back to care team
            </Link>

            <div className='mb-6'>
                <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Add a provider</h1>
                <p className='text-sm text-neutral-600 mt-1'>
                    Invite a clinician to upload directly, or add them manually and upload their documents
                    yourself.
                </p>
            </div>

            {/* Mode toggle */}
            <div className='grid grid-cols-2 gap-2 mb-6'>
                <ModeCard
                    active={mode === 'invite'}
                    onClick={() => setMode('invite')}
                    icon={EnvelopeIcon}
                    title='Invite by email'
                    blurb='Send a secure upload link. Clinician uploads directly, no account needed on their side.'
                />
                <ModeCard
                    active={mode === 'manual'}
                    onClick={() => setMode('manual')}
                    icon={UserPlusIcon}
                    title='Add manually'
                    blurb="Add the provider's contact info and upload their documents yourself."
                />
            </div>

            {/* Form */}
            <div className='bg-white border border-neutral-200 rounded-2xl p-6 space-y-4'>
                <div>
                    <label className='block text-sm font-medium text-neutral-700 mb-1.5'>
                        Provider type
                    </label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value as ProviderType)}
                        className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm bg-white'
                    >
                        {(Object.keys(PROVIDER_TYPE_LABEL) as ProviderType[]).map((t) => (
                            <option key={t} value={t}>
                                {PROVIDER_TYPE_LABEL[t]}
                            </option>
                        ))}
                    </select>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <Field label='Clinician name' placeholder='Dr. Jane Smith' />
                    <Field label='Organization' placeholder='Sunshine Pediatric Therapy' />
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <Field label='Email' placeholder='jane.smith@example.com' type='email' />
                    <Field label='Phone (optional)' placeholder='(555) 555-5555' />
                </div>

                {mode === 'invite' ? (
                    <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900'>
                        We&apos;ll email a secure upload link. The clinician can upload care plans, evaluations, and
                        progress notes. Nothing is shared back with them about other providers.
                    </div>
                ) : (
                    <div className='p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-xs text-neutral-700'>
                        You&apos;ll upload documents for this provider yourself. You can switch to invite mode later.
                    </div>
                )}

                <div className='flex items-center justify-end gap-2 pt-2'>
                    <button
                        onClick={() => navigate('/admin/family-care-team-preview/team')}
                        className='px-4 py-2 text-sm font-medium text-neutral-700 hover:text-neutral-900'
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => navigate('/admin/family-care-team-preview/team')}
                        className='inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg'
                    >
                        {mode === 'invite' ? (
                            <>
                                <PaperAirplaneIcon className='w-4 h-4' />
                                Send invitation
                            </>
                        ) : (
                            <>
                                <UserPlusIcon className='w-4 h-4' />
                                Add provider
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}

function ModeCard({
    active,
    onClick,
    icon: Icon,
    title,
    blurb,
}: {
    active: boolean
    onClick: () => void
    icon: React.ComponentType<{ className?: string }>
    title: string
    blurb: string
}) {
    return (
        <button
            type='button'
            onClick={onClick}
            className={`p-4 text-left rounded-xl border-2 transition-colors ${
                active ? 'border-brand-500 bg-brand-50/50' : 'border-neutral-200 bg-white hover:border-neutral-300'
            }`}
        >
            <div className='flex items-center gap-2 mb-1'>
                <Icon className={`w-4 h-4 ${active ? 'text-brand-700' : 'text-neutral-500'}`} />
                <span className='text-sm font-semibold text-neutral-900'>{title}</span>
            </div>
            <p className='text-xs text-neutral-600'>{blurb}</p>
        </button>
    )
}

function Field({
    label,
    placeholder,
    type = 'text',
}: {
    label: string
    placeholder: string
    type?: string
}) {
    return (
        <div>
            <label className='block text-sm font-medium text-neutral-700 mb-1.5'>{label}</label>
            <input
                type={type}
                placeholder={placeholder}
                className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm'
            />
        </div>
    )
}
